import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBuilding, FiBarChart3, FiClock } = FiIcons;

const CompanyGrid = ({ companies, loading }) => {
  if (loading) {
    return (
      <div className="grid-responsive">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="animate-pulse">
              <div className="w-16 h-16 bg-gray-200 rounded-lg mb-4"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded mb-4 w-3/4"></div>
              <div className="flex justify-between">
                <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/4"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <div className="text-center py-12">
        <SafeIcon icon={FiBuilding} className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No companies found</h3>
        <p className="mt-1 text-sm text-gray-500">You don't have access to any companies yet.</p>
      </div>
    );
  }

  return (
    <div className="grid-responsive">
      {companies.map((company, index) => (
        <motion.div
          key={company.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
        >
          <Link
            to={`/companies/${company.id}`}
            className="block bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 card-hover focus-ring"
          >
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    {company.logo ? (
                      <div className="h-16 w-16 rounded-lg border border-gray-200 bg-white flex items-center justify-center p-2">
                        <img
                          src={company.logo}
                          alt={`${company.name} logo`}
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="h-16 w-16 rounded-lg bg-primary-100 flex items-center justify-center" style={{ display: 'none' }}>
                          <SafeIcon icon={FiBuilding} className="h-8 w-8 text-primary-600" />
                        </div>
                      </div>
                    ) : (
                      <div className="h-16 w-16 rounded-lg bg-primary-100 flex items-center justify-center">
                        <SafeIcon icon={FiBuilding} className="h-8 w-8 text-primary-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {company.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {company.description}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <SafeIcon icon={FiBarChart3} className="h-4 w-4" />
                  <span>{company.assetsCount} assets</span>
                </div>
                <div className="flex items-center space-x-1">
                  <SafeIcon icon={FiClock} className="h-4 w-4" />
                  <span>{formatDistanceToNow(new Date(company.lastUpdated), { addSuffix: true })}</span>
                </div>
              </div>
            </div>
          </Link>
        </motion.div>
      ))}
    </div>
  );
};

export default CompanyGrid;